import type { Passage } from "./types";

export const passage: Passage = {
  slug: "isaiah-40-31",
  reference: "Isaiah 40:31",
  title: "Isaiah 40:31: wings like eagles, and the order of the list",
  h1: "Isaiah 40:31, and why the images get smaller",
  description:
    "Mount up with wings like eagles, run and not be weary, walk and not faint. The list descends, and the least impressive item is the one it is building toward.",
  targets: [
    "isaiah 40 31 meaning",
    "they that wait upon the lord explained",
    "wings like eagles meaning",
  ],
  published: "2026-09-03",
  standfirst:
    "Every motivational use of this verse stops at the eagles. The verse does not, and the direction it goes afterwards is the point.",
  verse: {
    text: "but those who wait for Yahweh will renew their strength. They will mount up with wings like eagles. They will run, and not be weary. They will walk, and not faint.",
    ref: "Isaiah 40:31",
  },
  context: [
    {
      text: "Haven't you known? Haven't you heard? The everlasting God, Yahweh, the Creator of the ends of the earth, doesn't faint. He isn't weary. His understanding is unsearchable. He gives power to the weak. He increases the strength of him who has no might. Even the youths faint and get weary, and the young men utterly fall;",
      ref: "Isaiah 40:28–30",
    },
    {
      text: "Comfort, comfort my people, says your God.",
      ref: "Isaiah 40:1",
    },
  ],
  takeaways: [
    "The three images descend — flying, running, walking — which is the opposite of how a motivational passage would build. Walking without fainting is the climax.",
    "Verse 30 sets it up by saying the young and fit fall over. The contrast is not between strong people and weak people; it is between everyone and God.",
    "Wait for is closer to a held expectancy than to an empty afternoon; the same root is used for a rope pulled taut.",
    "The chapter opens with comfort my people, addressed to exiles a long way into something with no end date. That is the audience.",
  ],
  body: [
    {
      type: "h2",
      id: "the-list-descends",
      text: "The list descends",
    },
    {
      type: "p",
      text: "Eagles, then running, then walking. Read as escalation it is anticlimactic, which is why almost every reproduction of the verse stops at the first image. Read as intentional, the order is the argument: the promise ends at the least spectacular thing a body can do.",
    },
    {
      type: "p",
      text: "That is the correct order for the audience. People at the start of a hard thing want to fly. People in year six of one want to still be upright at the end of the day, and the verse finishes where they are.",
    },
    {
      type: "h2",
      id: "who-faints-in-this-passage",
      text: "Who faints in this passage",
    },
    {
      type: "p",
      text: "Verse 30 is the setup and it is unusually blunt: even the youths faint and get weary, and the young men utterly fall. The category being described is not the frail. It is the fittest people available, and they go down too.",
    },
    {
      type: "p",
      text: "Which relocates the contrast. It is not between people who are strong and people who are not; it is between every human being and the God of verse 28, who does not grow tired. Nothing in the passage is asking the reader to try harder.",
    },
    {
      type: "p",
      text: "The verb translated renew is worth a moment too. It carries the sense of exchanging one thing for another — the way clothes are changed — rather than of topping something up. What is described is not a refill of the strength that ran out but a substitution of a different kind of strength, which fits a passage whose whole argument is that human capacity is not the relevant variable.",
    },
    {
      type: "h2",
      id: "the-audience",
      text: "The audience",
    },
    {
      type: "p",
      text: "Chapter 40 is where the book of Isaiah turns. The first thirty-nine chapters address Judah before the catastrophe; from here the audience is people already in exile, and the first line is comfort, comfort my people.",
    },
    {
      type: "p",
      text: "So this is not a locker-room verse. It is written to people whose situation had already gone wrong and was not going to be reversed quickly, which is why it promises endurance rather than victory.",
    },
  ],
  misreading: {
    claim:
      "That it promises energy and momentum — that waiting on God produces the strength to soar, achieve and outperform, with the eagle as the operative image.",
    correction: [
      "The eagle is the first image and the most quotable, and stopping there produces a verse about capability. Used that way it works for about a fortnight and then becomes an accusation: if you are not soaring, you must not have waited properly.",
      "The verse itself goes the other way. It descends to running, then to walking, and the walking is qualified only by not fainting — which is the smallest and most durable promise in the sentence. Held against verse 30, where the fittest people fall over, the point is plainly not performance.",
      "There is also the word wait, which in English suggests inactivity and in Hebrew is closer to a held tension, the strain in a rope. The renewal is promised to people who are still holding on, which is an active state and a tiring one, and the promise made to them is that they will still be walking.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Read verses 28 to 31 together. Three verses of setup and the famous line lands differently — particularly verse 30, which is the one that removes any suggestion that this is about your effort. It is four sentences and it takes twenty seconds, and it is the difference between a poster and an argument.",
    },
    {
      type: "p",
      text: "Use it in long things rather than hard days. It is a verse for month eleven, and it pairs with {a prayer that asks for the day} rather than for the season.",
      links: {
        "a prayer that asks for the day": "/prayers/prayer-for-strength",
      },
    },
    {
      type: "note",
      text: "The chapter around it is doing the same work at larger scale. It spends its middle section on how small the nations are — a drop in a bucket, dust on the scales — and then, having established that, turns to a specific exhausted people and says their strength will be renewed. The comfort is not that they are important. It is that the one who is not tired has noticed them anyway.",
    },
    {
      type: "note",
      text: "If the eagle line has stopped meaning anything through repetition, read only the last clause for a week. They will walk, and not faint.",
    },
  ],
  faq: [
    {
      q: "What does Isaiah 40:31 mean?",
      a: "That those who wait on God are given renewed strength — described in three descending images ending at walking without fainting, which is the climax rather than an afterthought.",
    },
    {
      q: "Why does the list go from flying to walking?",
      a: "Because the promise is about endurance rather than performance. The passage is addressed to exiles in something long, and it finishes at the smallest and most durable thing: still upright at the end of the day.",
    },
    {
      q: "What does wait upon the Lord mean?",
      a: "The Hebrew root suggests tension rather than idleness — the strain in a rope pulled taut. It describes an active, held expectancy rather than doing nothing until something happens.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so the verse is printed with the sentence it completes. It renders the divine name — 'those who wait for Yahweh will renew their strength' — where most translations have 'the LORD'.",
    },
  ],
  related: ["digital-sabbath", "how-to-memorize-bible-verses"],
  relatedTopics: ["bible-verses-about-strength", "bible-verses-about-rest"],
  relatedPassages: ["isaiah-41-10", "philippians-4-13"],
};
